import useFetchUsers from "api/UserResource/useFetchUsers";
import { DataTable } from "primereact/datatable";
import { useFormik } from "formik";
import { Column } from "primereact/column";
import { useState, useEffect } from "react";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { BsPencil, BsSearch } from "react-icons/bs";
import useAuthStore from "store/authStore";
import useAssignBooksToUsers from "api/BookResource/useAssignBooksToUsers";
import { Dialog } from "primereact/dialog";
import { FiArrowRight, FiX, FiUsers, FiCheckCircle, FiAlertCircle } from "react-icons/fi";

const AssignUsers = ({ books, className, children }) => {
	const { userProfile } = useAuthStore();

	const [display, setDisplay] = useState(false);

	const [searchTerm, setSearchTerm] = useState("");
	const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

	// Debounce search term
	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedSearchTerm(searchTerm);
		}, 500);

		return () => clearTimeout(timer);
	}, [searchTerm]);

	const { data: usersData, isLoading: isLoadingUsers } = useFetchUsers(
		{
			page: 1,
			rows_per_page: 10,
			search: debouncedSearchTerm,
			"filter[role_is]": "Student",
			"filter[institution_is]": userProfile?.institutions[0]?.id,
		},
		true
	);

	const [selectedUsers, setSelectedUsers] = useState([]);

	// Reset selected users when dialog opens
	useEffect(() => {
		if (display) {
			setSelectedUsers([]);
			setSearchTerm("");
		}
	}, [display]);

	const users = usersData?.data?.records;

	const { mutateAsync, isLoading: isAssigningBooks } = useAssignBooksToUsers();

	const { values, handleSubmit, setFieldValue } = useFormik({
		initialValues: {
			users: [...selectedUsers?.map((user) => user.id)],
			books: [...books],
		},
		onSubmit: async (data) => {
			// Filter books that have enough available seats for selected users
			const books = data.books
				.filter((book) => {
					const available = Number(book.available_to_assign ?? book.number_assignable ?? 0);
					return available >= data.users.length;
				})
				.map((book) => book.id);

			const payload = {
				users: data.users,
				books,
			};
			await mutateAsync(payload);

			setDisplay(false);
		},

		enableReinitialize: true,
	});

	const notAssignable = (book) => {
		const assignable = Number(book?.available_to_assign ?? book?.number_assignable ?? 0);
		return values?.users?.length > assignable || assignable <= 0;
	};

	const getBookAssignmentInfo = (book) => {
		const copiesSubscribed = Number(book?.copies_subscribed ?? 0);
		const subscriptionSeatsAssigned = Number(book?.subscription_seats_assigned ?? 0);
		const availableToAssign = Number(book?.available_to_assign ?? book?.number_assignable ?? 0);

		return {
			copiesSubscribed,
			availableToAssign,
			alreadyAssigned: subscriptionSeatsAssigned,
			hasSubscription: copiesSubscribed > 0,
		};
	};

	const assignableBooksCount = books?.filter((book) => !notAssignable(book))?.length || 0;

	return (
		<>
			<button
				type="button"
				className={`appearance-none border-0 ${className} `}
				onClick={() => setDisplay(true)}
			>
				{children || <BsPencil size={15} />}
			</button>

			<Dialog
				visible={display}
				onHide={() => setDisplay(false)}
				breakpoints={{
					"960px": "90vw",
					"0px": "100vw",
				}}
				className="font-sans"
				showHeader={false}
				contentClassName="rounded-md relative"
				blockScroll={false}
			>
				<button
					type="button"
					onClick={() => setDisplay(false)}
					className="absolute top-4 right-4 hover:bg-gray-100 rounded-full p-2 transition-colors z-10"
				>
					<FiX size={24} />
				</button>

				<div className="pt-8 pb-6">
					{/* Header Section */}
					<div className="px-6 pb-4 border-b border-gray-200">
						<h3 className="font-cera-black text-akgreenest text-2xl mb-2">
							Assign Books to Students
						</h3>
						<p className="text-gray-600 text-sm">
							Select students below to give them access to the selected books
						</p>
					</div>

					{/* Summary Cards */}
					<div className="grid grid-cols-3 gap-4 px-6 py-4">
						<div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
							<div className="flex items-center gap-2 text-blue-700 mb-1">
								<FiCheckCircle size={18} />
								<span className="text-sm font-medium">Books Selected</span>
							</div>
							<p className="text-2xl font-bold text-blue-900">{books?.length || 0}</p>
						</div>
						<div className="bg-green-50 border border-green-200 rounded-lg p-4">
							<div className="flex items-center gap-2 text-green-700 mb-1">
								<FiCheckCircle size={18} />
								<span className="text-sm font-medium">Assignable Books</span>
							</div>
							<p className="text-2xl font-bold text-green-900">{assignableBooksCount}</p>
						</div>
						<div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
							<div className="flex items-center gap-2 text-purple-700 mb-1">
								<FiUsers size={18} />
								<span className="text-sm font-medium">Students Selected</span>
							</div>
							<p className="text-2xl font-bold text-purple-900">{selectedUsers?.length || 0}</p>
						</div>
					</div>

					{/* Main Content */}
					<div className="px-6 py-4">
						<form onSubmit={handleSubmit} className="space-y-6">
							{/* Books List */}
							<div className="bg-gray-50 rounded-lg p-4">
								<h4 className="font-bold text-gray-700 mb-3 flex items-center gap-2">
									<span>📚</span>
									<span>Books to Assign</span>
								</h4>
								<div className="grid grid-cols-1 gap-2 max-h-48 overflow-y-auto">
									{books?.map((book) => {
										const info = getBookAssignmentInfo(book);
										return (
											<div
												key={book.id}
												className={`bg-white border rounded-lg p-3 ${
													notAssignable(book) ? "border-red-300 bg-red-50" : "border-green-300"
												}`}
											>
												<div className="flex items-start justify-between mb-2">
													<div className="flex-1 min-w-0">
														<p
															className={`font-medium truncate ${
																notAssignable(book) ? "text-gray-400 line-through" : "text-gray-900"
															}`}
														>
															{book?.title}
														</p>
														<p className="text-sm text-gray-500">by {book?.author}</p>
													</div>
													<div className="ml-3 flex-shrink-0">
														{notAssignable(book) ? (
															<span className="inline-flex items-center gap-1 bg-red-100 text-red-700 px-3 py-1 rounded-full text-xs font-medium">
																<FiAlertCircle size={14} />
																Not assignable
															</span>
														) : (
															<span className="inline-flex items-center gap-1 bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-medium">
																<FiCheckCircle size={14} />
																Available
															</span>
														)}
													</div>
												</div>
												{info.hasSubscription && (
													<div className="flex items-center gap-3 text-xs text-gray-600 mt-1 pt-2 border-t border-gray-200">
														<span className="flex items-center gap-1">
															<span className="font-medium">Total seats:</span>
															<span className="text-blue-600 font-semibold">
																{info.copiesSubscribed}
															</span>
														</span>
														<span className="flex items-center gap-1">
															<span className="font-medium">Assigned:</span>
															<span className="text-orange-600 font-semibold">
																{info.alreadyAssigned}
															</span>
														</span>
														<span className="flex items-center gap-1">
															<span className="font-medium">Available:</span>
															<span className="text-green-600 font-semibold">
																{info.availableToAssign}
															</span>
														</span>
													</div>
												)}
											</div>
										);
									})}
								</div>
								{values?.users?.length > 0 && (
									<p className="text-xs text-gray-500 mt-3 italic">
										Note: Books are not assignable if remaining copies are less than the number of
										selected students
									</p>
								)}
							</div>

							{/* Search Section */}
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-2">
									Search and Select Students
								</label>
								<div className="relative">
									<input
										type="text"
										name="searchTerm"
										placeholder="Type to search students by name or email..."
										className="w-full h-12 border border-gray-300 rounded-lg pr-4 pl-11 focus:ring-2 focus:ring-akgreener focus:border-transparent"
										value={searchTerm}
										onChange={(e) => setSearchTerm(e.target.value)}
									/>
									<span className="absolute left-4 top-1/2 -translate-y-1/2">
										{isLoadingUsers ? (
											<AiOutlineLoading3Quarters size={18} className="text-gray-400 animate-spin" />
										) : (
											<BsSearch size={18} className="text-gray-400" />
										)}
									</span>
								</div>
							</div>

							{/* Students Table */}
							<div className="border border-gray-200 rounded-lg overflow-hidden">
								{users && users.length > 0 ? (
									<DataTable
										value={users}
										selection={selectedUsers}
										onSelectionChange={(e) => {
											setSelectedUsers(e.value);
											setFieldValue("users", [...e.value?.map((user) => user?.id)]);
										}}
										loading={isLoadingUsers}
										tableClassName="font-sans text-sm"
										className="w-full"
										autoLayout
									>
										<Column selectionMode="multiple" headerStyle={{ width: "3em" }}></Column>
										<Column field="first_name" header="First Name" sortable />
										<Column field="last_name" header="Last Name" sortable />
										<Column field="email" header="Email" sortable />
									</DataTable>
								) : !isLoadingUsers ? (
									<div className="py-12 text-center text-gray-500">
										<FiUsers size={48} className="mx-auto mb-3 text-gray-300" />
										<p className="font-medium">No students found</p>
										<p className="text-sm">
											{searchTerm
												? "Try a different search term"
												: "Start typing to search for students"}
										</p>
									</div>
								) : null}
							</div>

							{/* Action Buttons */}
							<div className="flex justify-end gap-3 pt-4 border-t">
								<button
									type="button"
									onClick={() => setDisplay(false)}
									className="px-6 py-3 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors font-medium"
								>
									Cancel
								</button>
								<button
									type="submit"
									disabled={
										isAssigningBooks || selectedUsers.length === 0 || assignableBooksCount === 0
									}
									className="flex items-center gap-2 px-6 py-3 rounded-lg bg-akgreener text-white hover:bg-green-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed font-medium"
								>
									{isAssigningBooks ? (
										<>
											<AiOutlineLoading3Quarters size={20} className="animate-spin" />
											Assigning...
										</>
									) : (
										<>
											Assign {assignableBooksCount} Book{assignableBooksCount !== 1 ? "s" : ""} to{" "}
											{selectedUsers.length} Student{selectedUsers.length !== 1 ? "s" : ""}
											<FiArrowRight size={20} />
										</>
									)}
								</button>
							</div>
						</form>
					</div>
				</div>
			</Dialog>
		</>
	);
};

export default AssignUsers;
